import { CommonChartProps } from '../../types';
import { Bar, BarProps, BarChart as RechartsBarChart } from 'recharts';
import { BaseChart } from '../../BaseChart';
import { getAdjustedOpacity } from '../../utils';

export type ExtendedBarProps = React.PropsWithoutRef<BarProps>;
/**
 * Props for the BarChart component.
 * @template TData The type of data items in the chart.
 */
export type BarChartProps<TData> = CommonChartProps<
  TData,
  React.ComponentProps<typeof RechartsBarChart> & { data?: TData[] },
  ExtendedBarProps
>;

/**
 * BarChart component that extends the BaseChart with Recharts' BarChart.
 *
 * @template TData - The type of data items.
 *
 * @param props - The props for the BarChart component.
 * @returns A JSX element representing the BarChart.
 */
export const BarChart = <TData,>(props: BarChartProps<TData>) => {
  return (
    <BaseChart
      {...props}
      chartType="bar"
      ChartComponent={RechartsBarChart}
      renderSeries={({ normalizedSeries, activeIndex }) =>
        normalizedSeries.map(({ dataKey, color, ...seriesProps }, index) => {
          const adjustedRadius = adjustStackedBarRadius(
            seriesProps.radius,
            seriesProps.stackId,
            index,
            normalizedSeries.length,
          );

          return (
            <Bar
              key={dataKey}
              fill={color}
              stroke={color}
              {...seriesProps}
              radius={adjustedRadius ?? seriesProps.radius}
              dataKey={dataKey}
              fillOpacity={getAdjustedOpacity(
                activeIndex,
                index,
                seriesProps.fillOpacity,
              )}
              strokeOpacity={getAdjustedOpacity(
                activeIndex,
                index,
                seriesProps.strokeOpacity,
              )}
            />
          );
        })
      }
    />
  );
};

/**
 * Adjusts the radius for stacked bars to ensure proper styling.
 *
 * @param radius - The original radius value.
 * @param stackId - The stack ID to determine stacking.
 * @param index - The index of the current bar.
 * @param totalBars - The total number of bars in the stack.
 * @returns An adjusted radius array or undefined.
 */
const adjustStackedBarRadius = (
  radius: number | undefined | [number, number, number, number],
  stackId: string | undefined | number,
  index: number,
  totalBars: number,
): [number, number, number, number] | undefined => {
  if (stackId && typeof radius === 'number') {
    const isFirst = index === 0;
    const isLast = index === totalBars - 1;

    return [
      isLast ? radius : 0,
      isLast ? radius : 0,
      isFirst ? radius : 0,
      isFirst ? radius : 0,
    ];
  }

  return undefined;
};
